import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    IconButton,
    TextField,
    Typography
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { updateMeasurement, deleteMeasurement } from "../services/BodyMeasurementsService";
import { RTL } from "../cache/CacheRtl";
import { parseMediaDate } from "../utils/progressMediaDate";

// Same calendar-day rule as BodyMeasurementsTablePage: parse with no timezone involvement.
// `new Date("2026-07-15")` parses as UTC midnight and the local getters below then read it in the
// viewer's zone, showing the previous day west of UTC — on the header a trainer reads right before
// overwriting a value.
export const formatFullDate = (dateString) => {
    const date = parseMediaDate(dateString);
    if (!date) return '—';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const EditMeasurementModal = ({ open, onClose, cell, onSave, notificationHandler }) => {
    const [editValue, setEditValue] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (cell) {
            setEditValue(String(cell.value));
        }
    }, [cell]);

    const handleSave = async () => {
        const parsed = parseFloat(editValue);
        if (isNaN(parsed) || parsed <= 0) return;

        setSaving(true);
        try {
            await updateMeasurement(cell.id, parsed, null);
            notificationHandler.success('המדידה עודכנה בהצלחה');
            onSave();
            onClose();
        } catch (err) {
            console.error('Error updating measurement:', err);
            notificationHandler.error('שגיאה בעדכון המדידה');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('האם למחוק מדידה זו?')) return;

        setSaving(true);
        try {
            await deleteMeasurement(cell.id);
            notificationHandler.success('המדידה נמחקה בהצלחה');
            onSave();
            onClose();
        } catch (err) {
            console.error('Error deleting measurement:', err);
            notificationHandler.error('שגיאה במחיקת המדידה');
        } finally {
            setSaving(false);
        }
    };

    if (!cell) return null;

    const typeName = cell.definitionName || '';
    const unit = cell.unit || 'ס״מ';

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "16px"
                }
            }}
        >
            <RTL>
                <Box dir="rtl">
                    {/* Header */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 20px',
                        borderBottom: '1px solid #E2E8F0'
                    }}>
                        <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1E293B" }}>
                            עריכת מדידה
                        </Typography>
                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{ color: "#64748B", "&:hover": { backgroundColor: "#F1F5F9", color: "#1E293B" } }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    {/* Content */}
                    <DialogContent sx={{ padding: '24px 20px' }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ fontSize: '14px', color: '#64748B', mb: 0.5 }}>
                                סוג: <span style={{ color: '#1E293B', fontWeight: 600 }}>{typeName}</span>
                            </Typography>
                            <Typography sx={{ fontSize: '14px', color: '#64748B' }}>
                                תאריך: <span style={{ color: '#1E293B', fontWeight: 600 }}>{formatFullDate(cell.measurementDate)}</span>
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                            <TextField
                                fullWidth
                                type="number"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSave()}
                                disabled={saving}
                                size="small"
                                inputProps={{ dir: "rtl", step: "0.1", min: "0" }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: "8px",
                                        backgroundColor: "#F8FAFC",
                                        '& fieldset': {
                                            borderColor: "#E2E8F0"
                                        }
                                    }
                                }}
                            />
                            <Typography sx={{ fontSize: '14px', color: '#64748B', whiteSpace: 'nowrap' }}>
                                {unit}
                            </Typography>
                        </Box>
                    </DialogContent>

                    {/* Footer */}
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 20px',
                        borderTop: '1px solid #E2E8F0',
                        backgroundColor: '#F8FAFC'
                    }}>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={saving || !editValue || parseFloat(editValue) <= 0}
                                sx={{
                                    backgroundColor: '#14B8A6',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    padding: '8px 24px',
                                    boxShadow: 'none',
                                    '&:hover': { backgroundColor: '#0D9488', boxShadow: 'none' },
                                    '&.Mui-disabled': { backgroundColor: '#E2E8F0', color: '#94A3B8' }
                                }}
                            >
                                שמור
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                disabled={saving}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    borderColor: '#E2E8F0',
                                    color: '#64748B',
                                    fontWeight: 500,
                                    fontSize: '13px',
                                    padding: '8px 20px',
                                    '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#fff' }
                                }}
                            >
                                ביטול
                            </Button>
                        </Box>
                        <Button
                            variant="text"
                            startIcon={<DeleteIcon />}
                            onClick={handleDelete}
                            disabled={saving}
                            sx={{
                                color: '#EF4444',
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '13px',
                                '&:hover': { backgroundColor: '#FEF2F2' }
                            }}
                        >
                            מחיקת מדידה
                        </Button>
                    </Box>
                </Box>
            </RTL>
        </Dialog>
    );
};

export default EditMeasurementModal;
